import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, ChevronDown, Heart, ImagePlus, Minus, Plus, RotateCcw, ShieldCheck, ShoppingBag, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { api, discountOf, fileUrl, fmt } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useShop } from "@/context/ShopContext";
import ProductCard from "@/components/ProductCard";
import NotifyButton from "@/components/NotifyButton";
import { Reveal } from "@/components/Reveal";

function Lightbox({ src, alt, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <motion.div
      data-testid="pdp-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[80] bg-kay-ink/85 backdrop-blur-md flex items-center justify-center p-5 cursor-zoom-out"
    >
      <motion.img
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 220, damping: 24 }}
        src={src}
        alt={alt}
        className="max-h-[85vh] max-w-full rounded-2xl shadow-2xl object-contain"
      />
      <button data-testid="pdp-lightbox-close" onClick={onClose} aria-label="Fermer" className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-kay-ink transition-colors">
        <X size={18} />
      </button>
    </motion.div>
  );
}

function AccordionItem({ title, icon: Icon, children, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-kay-line">
      <button type="button" onClick={() => setOpen(!open)} data-testid={testid} className="w-full flex items-center justify-between py-4 text-left group">
        <span className="flex items-center gap-3 font-semibold text-sm group-hover:text-kay-pink transition-colors">
          <Icon size={16} className="text-kay-pink shrink-0" /> {title}
        </span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }}>
          <ChevronDown size={16} className="text-kay-ink/40" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-5 pl-7 text-sm text-kay-ink/60 leading-relaxed whitespace-pre-line" data-testid={`${testid}-content`}>{children}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Swatch({ v, index, active, onSelect }) {
  const out = v.stock <= 0;
  return (
    <button
      type="button"
      data-testid={`pdp-variant-${index}`}
      onClick={() => !out && onSelect(index)}
      disabled={out}
      title={out ? `${v.name} — épuisé` : v.name}
      className={`group relative flex flex-col items-center gap-1.5 ${out ? "cursor-not-allowed" : "cursor-pointer"}`}
    >
      <motion.span
        whileTap={out ? {} : { scale: 0.9 }}
        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 transition-all duration-300 flex items-center justify-center font-display italic text-sm ${
          out
            ? "border-kay-line opacity-40 grayscale"
            : active
              ? "border-kay-ink ring-2 ring-kay-pink ring-offset-2 scale-110"
              : "border-kay-line hover:border-kay-pink hover:scale-105"
        }`}
      >
        {v.image ? (
          <img src={fileUrl(v.image)} alt={v.name} className="w-full h-full object-cover" />
        ) : (
          <span className="w-full h-full bg-gradient-to-br from-kay-rose to-kay-rosedeep/50 flex items-center justify-center text-kay-wine/70">
            {v.name.charAt(0)}
          </span>
        )}
      </motion.span>
      <span className={`text-[10px] font-mono uppercase tracking-wider ${out ? "line-through text-kay-ink/35" : active ? "text-kay-pink font-bold" : "text-kay-ink/50"}`}>
        {out ? "Épuisé" : v.name}
      </span>
    </button>
  );
}

export default function ProductPage() {
  const { pid } = useParams();
  const { add } = useCart();
  const { settings, maintenance } = useShop() || {};
  const [product, setProduct] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [sel, setSel] = useState(null);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    setProduct(null);
    setSel(null);
    setQty(1);
    setImgIdx(0);
    api.get(`/products/${pid}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
    api.get("/products").then((r) => setAll(r.data.filter((p) => !p.coming_soon))).catch(() => {});
    window.scrollTo(0, 0);
  }, [pid]);

  const variants = product?.variants || [];
  const selV = sel != null ? variants[sel] : null;

  const gallery = useMemo(() => {
    if (!product) return [];
    const imgs = [...(product.images || [])];
    variants.forEach((v) => v.image && !imgs.includes(v.image) && imgs.push(v.image));
    return imgs;
  }, [product, variants]);

  // Quand une teinte avec photo dédiée est choisie, elle devient l'image principale
  useEffect(() => {
    if (selV?.image) {
      const idx = gallery.indexOf(selV.image);
      if (idx >= 0) setImgIdx(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);

  const currentStock = variants.length ? (selV ? selV.stock : null) : product?.stock ?? 0;
  const allOut = variants.length ? variants.every((v) => v.stock <= 0) : (product?.stock ?? 0) <= 0;
  const d = product ? discountOf(product) : 0;

  const similar = useMemo(() => {
    if (!product) return [];
    const score = (p) =>
      (p.subcategory && p.subcategory === product.subcategory ? 2 : 0) +
      (p.brand === product.brand ? 2 : 0) +
      (p.category === product.category ? 1 : 0);
    return all
      .filter((p) => p.id !== product.id)
      .map((p) => [score(p), p])
      .filter(([s]) => s > 0)
      .sort((a, b) => b[0] - a[0])
      .slice(0, 4)
      .map(([, p]) => p);
  }, [all, product]);

  const handleAdd = () => {
    if (maintenance) return toast.error("La boutique est en maintenance — commandes désactivées");
    if (variants.length && !selV) return toast.error("Sélectionne ta teinte ♡");
    if (currentStock != null && qty > currentStock) return toast.error("Stock insuffisant pour cette teinte");
    add(product, selV, qty);
    toast.success(`${product.name}${selV ? ` (${selV.name})` : ""} ajouté au panier`);
  };

  if (notFound) {
    return (
      <div className="py-32 text-center" data-testid="pdp-not-found">
        <p className="font-display italic text-3xl text-kay-ink/50">Cette pépite n'existe plus…</p>
        <Link to="/boutique" className="mt-6 inline-block rounded-full bg-kay-ink text-white px-7 py-3 text-sm font-semibold hover:bg-kay-pink transition-colors">
          Retour à la boutique
        </Link>
      </div>
    );
  }
  if (!product) {
    return <div className="py-32 text-center font-display italic text-2xl text-kay-ink/40" data-testid="pdp-loading">Chargement de la pépite…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-8 lg:pt-12 pb-16" data-testid="product-page">
      <nav className="font-mono text-[10px] uppercase tracking-[0.25em] text-kay-ink/40 mb-8" data-testid="pdp-breadcrumb">
        <Link to="/boutique" className="hover:text-kay-pink">Boutique</Link>
        <span className="mx-2">/</span>
        <Link to={`/boutique/${product.category}`} className="hover:text-kay-pink capitalize">{product.category}</Link>
        <span className="mx-2">/</span>
        <span className="text-kay-pink">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Galerie */}
        <Reveal>
          <div
            data-testid="pdp-main-image"
            onClick={() => gallery.length > 0 && setZoom(true)}
            className="relative aspect-[4/5] rounded-3xl border border-kay-line bg-white overflow-hidden shadow-[0_20px_60px_rgba(232,160,191,0.18)] cursor-zoom-in"
          >
            <AnimatePresence mode="wait">
              {gallery.length > 0 ? (
                <motion.img
                  key={gallery[Math.min(imgIdx, gallery.length - 1)]}
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  src={fileUrl(gallery[Math.min(imgIdx, gallery.length - 1)])}
                  alt={selV ? `${product.name} — ${selV.name}` : product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-4 border border-dashed border-kay-rosedeep/60 rounded-2xl flex flex-col items-center justify-center gap-3 bg-kay-rose/40">
                  <ImagePlus className="text-kay-pink/70" size={36} strokeWidth={1.5} />
                  <p className="font-display italic text-xl text-kay-ink/50">Photo à venir</p>
                </div>
              )}
            </AnimatePresence>
            {d > 0 && (
              <span className="absolute top-4 left-4 bg-kay-pink text-white font-mono text-xs font-bold tracking-widest px-3 py-1.5 rounded-full" data-testid="pdp-discount-badge">
                -{d} %
              </span>
            )}
            {product.coming_soon && (
              <span className="absolute top-4 left-4 bg-kay-ink text-kay-rose font-mono text-xs font-bold tracking-widest px-3 py-1.5 rounded-full">COMING SOON</span>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex gap-3 flex-wrap" data-testid="pdp-thumbnails">
              {gallery.map((g, i) => (
                <button
                  key={g}
                  type="button"
                  data-testid={`pdp-thumb-${i}`}
                  onClick={() => setImgIdx(i)}
                  className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                    i === Math.min(imgIdx, gallery.length - 1) ? "border-kay-ink scale-105" : "border-kay-line opacity-60 hover:opacity-100 hover:border-kay-pink"
                  }`}
                >
                  <img src={fileUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </Reveal>

        {/* Informations */}
        <Reveal delay={0.1} className="flex flex-col">
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-kay-pink" data-testid="pdp-brand">{product.brand}</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] mt-2" data-testid="pdp-name">{product.name}</h1>

          {product.price != null && (
            <div className="mt-5 flex items-end gap-3 flex-wrap">
              <span className="font-display text-4xl font-semibold" data-testid="pdp-price">{fmt(product.price)}</span>
              {product.reference_price > product.price && (
                <>
                  <span className="font-mono text-base text-kay-ink/40 line-through mb-1" data-testid="pdp-ref-price">{fmt(product.reference_price)}</span>
                  <span className="rounded-full bg-kay-rose px-3 py-1 font-mono text-[11px] font-bold text-kay-wine mb-1" data-testid="pdp-savings">
                    Tu économises {fmt(product.reference_price - product.price)}
                  </span>
                </>
              )}
            </div>
          )}

          {product.description && (
            <p className="mt-5 text-sm text-kay-ink/60 leading-relaxed" data-testid="pdp-description">{product.description}</p>
          )}

          {variants.length > 0 && (
            <div className="mt-8" data-testid="pdp-variants">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-kay-ink/50 mb-3">
                Sélectionne ta teinte <span className="text-kay-pink">♡</span>
                {selV && <span className="ml-2 text-kay-pink font-bold" data-testid="pdp-variant-selected">{selV.name}</span>}
              </p>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                {variants.map((v, i) => (
                  <Swatch key={v.name} v={v} index={i} active={sel === i} onSelect={(idx) => { setSel(idx); setQty(1); }} />
                ))}
              </div>
            </div>
          )}

          {!product.coming_soon && (
            <p className="mt-5 font-mono text-[11px] uppercase tracking-widest" data-testid="pdp-stock">
              {variants.length && !selV ? (
                <span className="text-kay-ink/40">Choisis une teinte pour voir son stock</span>
              ) : currentStock <= 0 ? (
                <span className="text-red-500 font-bold">Épuisé</span>
              ) : currentStock <= 3 ? (
                <span className="text-kay-pink font-bold">Plus que {currentStock} en stock — vite !</span>
              ) : (
                <span className="text-kay-green">En stock ({currentStock} dispo)</span>
              )}
            </p>
          )}

          {product.coming_soon || allOut ? (
            <div className="mt-7" data-testid="pdp-notify-zone">
              <NotifyButton product={product} testid={`pdp-notify-${product.id}`} />
              <p className="mt-2 text-center text-[11px] text-kay-ink/40">Tu recevras un email dès que cette pépite arrive.</p>
            </div>
          ) : (
            <div className="mt-7 flex items-stretch gap-3">
              <div className="flex items-center rounded-full border border-kay-line bg-white" data-testid="pdp-qty">
                <button type="button" data-testid="pdp-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-full flex items-center justify-center hover:text-kay-pink transition-colors" aria-label="Moins">
                  <Minus size={15} />
                </button>
                <span className="w-8 text-center font-display text-xl font-semibold" data-testid="pdp-qty-value">{qty}</span>
                <button
                  type="button"
                  data-testid="pdp-qty-plus"
                  onClick={() => setQty(Math.min(qty + 1, currentStock ?? 20))}
                  disabled={currentStock != null && qty >= currentStock}
                  className="w-11 h-full flex items-center justify-center hover:text-kay-pink transition-colors disabled:opacity-30"
                  aria-label="Plus"
                >
                  <Plus size={15} />
                </button>
              </div>
              <motion.button
                type="button"
                whileTap={{ scale: 0.97 }}
                data-testid="pdp-add-to-cart"
                onClick={handleAdd}
                disabled={!!maintenance || (currentStock != null && currentStock <= 0)}
                className="flex-1 rounded-full bg-kay-ink text-white py-4 text-sm font-bold flex items-center justify-center gap-2.5 transition-all hover:bg-kay-pink hover:shadow-[0_15px_40px_rgba(224,82,151,0.35)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ShoppingBag size={17} />
                {maintenance ? "Maintenance en cours" : variants.length && !selV ? "Choisis ta teinte" : "Ajouter au panier"}
              </motion.button>
            </div>
          )}

          <div className="mt-8 border-t border-kay-line" data-testid="pdp-accordion">
            <AccordionItem title="Description" icon={Heart} testid="pdp-acc-description" defaultOpen>
              {product.description || "Description à venir."}
            </AccordionItem>
            <AccordionItem title="Détails du produit" icon={BadgeCheck} testid="pdp-acc-details">
              {product.details || "Détails à venir."}
            </AccordionItem>
            <AccordionItem title="Livraison" icon={Truck} testid="pdp-acc-shipping">
              {settings?.policies?.shipping || "Informations de livraison à venir."}
            </AccordionItem>
            <AccordionItem title="Retours" icon={RotateCcw} testid="pdp-acc-returns">
              {settings?.policies?.returns || "Politique de retour à venir."}
            </AccordionItem>
            <AccordionItem title="Authenticité" icon={ShieldCheck} testid="pdp-acc-authenticity">
              {settings?.policies?.authenticity || "Tous nos produits sont authentiques."}
            </AccordionItem>
          </div>
        </Reveal>
      </div>

      {similar.length > 0 && (
        <section className="mt-24" data-testid="pdp-similar">
          <Reveal className="mb-8">
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-2">Continue ton shopping</p>
            <h2 className="font-display text-3xl sm:text-4xl">
              Tu pourrais aussi aimer <span className="italic text-kay-pink">♡</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {similar.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <AnimatePresence>
        {zoom && gallery.length > 0 && (
          <Lightbox src={fileUrl(gallery[Math.min(imgIdx, gallery.length - 1)])} alt={product.name} onClose={() => setZoom(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}
